import { Flex } from '@acsbe/ui';
import { FunctionComponent, PropsWithChildren, useId } from 'react';
import styled from 'styled-components';
import { BillingMethod } from '../../../context/BillingContext';

type CardGraphicProps = {
  small?: boolean;
  card: BillingMethod;
  disabled?: boolean;
};

const CardGraphicDetails = ({ cardDigits }: { cardDigits: string }) => (
  <>
    <circle cx="6" cy="17" r="0.85" fill="currentColor" opacity="0.4" />
    <circle cx="8.9" cy="17" r="0.85" fill="currentColor" opacity="0.4" />
    <circle cx="11.8" cy="17" r="0.85" fill="currentColor" opacity="0.4" />
    <circle cx="14.7" cy="17" r="0.85" fill="currentColor" opacity="0.4" />
    <text
      x="18.2"
      y="19.7"
      fill="currentColor"
      fontSize="53%"
      fontWeight="700"
      letterSpacing={-0.3}
    >
      {cardDigits}
    </text>
  </>
);

const ValidCardContainer: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const gradientId = useId();
  const maskId = useId();

  return (
    <svg
      width="40"
      height="24"
      viewBox="0 0 40 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.000366211"
        width="39.9998"
        height="23.2098"
        rx="4"
        fill={`url(#${gradientId})`}
      />
      <mask
        id={maskId}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="40"
        height="24"
      >
        <rect
          x="6.10352e-05"
          width="39.9998"
          height="23.2098"
          rx="4"
          fill="#ED713C"
        />
      </mask>

      <g
        mask={`url(#${maskId})`}
        style={{ mixBlendMode: 'overlay' }}
        opacity="0.78"
      >
        <path
          d="M32.225 29.0261C31.6154 29.0261 30.9767 29.0244 30.3107 29.0261C27.7346 29.028 25.1207 29.0294 22.6068 29.0294C19.7702 29.0294 17.8388 26.1571 18.9058 23.5289L30.3998 -4.78192C31.246 -6.73532 33.2658 -8.01135 35.5077 -8.01135H44.5515C46.4203 -8.01135 47.6959 -6.23072 46.9899 -4.60232L34.8944 27.2746C34.8799 27.3156 34.8617 27.355 34.8435 27.3943C34.5578 28.0341 34.0137 28.5472 33.3331 28.8158C33.264 28.8448 33.1912 28.8705 33.1166 28.8927C33.0438 28.915 32.9692 28.9355 32.8946 28.9526C32.8564 28.9612 32.82 28.968 32.7818 28.9765C32.7035 28.9902 32.6271 29.0022 32.5489 29.0108L32.225 29.0261Z"
          fill="#02388A"
        />
        <path
          d="M30.3102 29.0275C30.3102 29.027 30.3098 29.0266 30.3094 29.0266C25.613 29.0317 20.7876 29.0317 16.6735 29.0266H14.5973C13.5928 28.9599 12.7375 28.392 12.3045 27.5932L12.117 27.1536L5.01116 7.78559C4.31605 6.1589 5.58982 4.38853 7.455 4.38853H15.9675C18.2221 4.38853 20.251 5.67996 21.0881 7.64875L29.7752 27.34C30.1592 28.2432 30.9926 29.0334 32.0262 29.0283H30.3111C30.3106 29.0283 30.3102 29.0279 30.3102 29.0275V29.0275Z"
          fill="#16A5E1"
        />
        <path
          d="M29.7738 27.3398C30.1542 28.2343 30.973 29.0212 31.9975 29.028H30.3107C27.7362 29.0299 25.123 29.0307 22.6094 29.0306C19.7721 29.0304 17.8395 26.1572 18.9067 23.5283L19.7555 21.4377C21.0845 18.1639 25.6951 18.0951 27.1213 21.3277L29.7738 27.3398Z"
          fill="#2D7FFF"
        />
      </g>
      <defs>
        <linearGradient
          id={gradientId}
          x1="28.5717"
          y1="-1.00912"
          x2="19.9067"
          y2="24.653"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#136EF8" />
          <stop offset="1" stopColor="#0047D8" />
        </linearGradient>
      </defs>
      {children}
    </svg>
  );
};

const InvalidCardContainer: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const maskId = useId();

  return (
    <svg
      width="40"
      height="24"
      viewBox="0 0 40 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.000488281"
        width="39.9998"
        height="23.2098"
        rx="4"
        fill="#E8EBF3"
      />
      <mask
        id={maskId}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="40"
        height="24"
      >
        <rect width="39.9998" height="23.2098" rx="4" fill="#E8EBF3" />
      </mask>
      <g mask={`url(#${maskId})`} opacity="0.78">
        <path
          d="M32.2248 29.0261C31.6152 29.0261 30.9765 29.0244 30.3105 29.0261C27.7344 29.028 25.1206 29.0294 22.6067 29.0294C19.7701 29.0294 17.8387 26.1571 18.9057 23.5289L30.3997 -4.78192C31.2458 -6.73532 33.2657 -8.01135 35.5075 -8.01135H44.5514C46.4202 -8.01135 47.6958 -6.23072 46.9897 -4.60232L34.8943 27.2746C34.8798 27.3156 34.8616 27.355 34.8434 27.3943C34.5577 28.0341 34.0136 28.5472 33.333 28.8158C33.2639 28.8448 33.1911 28.8705 33.1165 28.8927C33.0437 28.915 32.9691 28.9355 32.8945 28.9526C32.8563 28.9612 32.8199 28.968 32.7817 28.9765C32.7034 28.9902 32.627 29.0022 32.5487 29.0108L32.2248 29.0261Z"
          fill="#D5DCEB"
        />
        <path
          d="M30.3101 29.0275C30.3101 29.027 30.3097 29.0266 30.3092 29.0266C25.6129 29.0318 20.7874 29.0318 16.6734 29.0266H14.5971C13.5927 28.9599 12.7374 28.392 12.3043 27.5932L12.1169 27.1536L5.01104 7.78561C4.31592 6.15892 5.5897 4.38855 7.45488 4.38855H15.9674C18.2219 4.38855 20.2509 5.67998 21.0879 7.64877L29.7751 27.3401C30.1591 28.2432 30.9925 29.0335 32.0261 29.0283H30.3109C30.3105 29.0283 30.3101 29.0279 30.3101 29.0275V29.0275Z"
          fill="#F7FAFD"
        />
      </g>
      {children}
    </svg>
  );
};

export const CardGraphic: FunctionComponent<CardGraphicProps> = ({
  small,
  card,
  disabled,
}) => {
  const width = small ? '40px' : '72px';
  const height = small ? '23px' : '42px';

  return (
    <CardContainer
      aria-hidden="true"
      role="presentation"
      width={width}
      minWidth={width}
      height={height}
      scale={disabled ? 'base' : 'invert'}
      variant={disabled ? 'element' : 'solid'}
    >
      {disabled ? (
        <InvalidCardContainer>
          <CardGraphicDetails cardDigits={card.cardDigits} />
        </InvalidCardContainer>
      ) : (
        <ValidCardContainer>
          <CardGraphicDetails cardDigits={card.cardDigits} />
        </ValidCardContainer>
      )}
    </CardContainer>
  );
};

const CardContainer = styled(Flex)`
  --fill: transparent;

  svg {
    width: 100%;
    height: 100%;
  }
`;
